import { getToken, logout } from '@/plugins/keycloak';
import { useNotificationStore } from '@/stores/notificationStore';
import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';

// Create base Axios instance
const api: AxiosInstance = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      config.headers['merchant-id'] = process.env.VUE_APP_MERCHANT_ID;
      config.headers['sso-domain'] = process.env.VUE_APP_SSO_DOMAIN;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor - handle errors
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError) => {
    // Handle 401 Unauthorized - token expired or invalid
    if (error.response?.status === 401) {
      console.error('Unauthorized - redirecting to login');
      logout();
      return Promise.reject(error);
    }

    // Handle 403 Forbidden - insufficient permissions
    if (error.response?.status === 403) {
      console.error('Forbidden - insufficient permissions');
      useNotificationStore().notify(
        'You do not have permission to perform this action.',
      );
    }

    // Handle 500 Internal Server Error
    if (error.response?.status === 500) {
      console.error('Internal server error');
      useNotificationStore().notify(
        'A server error occurred. Please try again later.',
      );
    }

    // Handle network errors
    if (!error.response) {
      console.error('Network error - no response from server');
      useNotificationStore().notify(
        'Network error. Please check your internet connection.',
      );
    }

    return Promise.reject(error);
  },
);

export default api;
